import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { UserGroupsResponse } from '@shared/models/user/user-group.model';
import { BaseHttpService } from './base-http.service';

export type UserGroupsStatus = 'idle' | 'loading' | 'refreshing' | 'loaded' | 'error';

export interface UserGroupsState {
  userId: string | number | null;
  status: UserGroupsStatus;
  items: UserGroupsResponse['data']['items'];
  error: unknown | null;
}

const USER_GROUPS_REQUEST_TIMEOUT_MS = 15_000;

@Injectable({
  providedIn: 'root',
})
export class UserGroupsService {
  private readonly stateSubject = new BehaviorSubject<UserGroupsState>({
    userId: null,
    status: 'idle',
    items: [],
    error: null,
  });
  readonly state$ = this.stateSubject.asObservable();
  private requestSubscription: Subscription | null = null;

  constructor(private baseHttp: BaseHttpService) {}

  get state(): UserGroupsState {
    return this.stateSubject.value;
  }

  setUser(userId: string | number | null | undefined): Observable<UserGroupsState> {
    if (userId === null || userId === undefined) {
      this.clear();
      return of(this.state);
    }

    if (this.sameUser(this.state.userId, userId)) {
      if (this.state.status === 'loading' || this.state.status === 'refreshing') {
        return this.whenSettled(userId);
      }
      if (this.state.status === 'loaded') {
        return of(this.state);
      }
    }

    // `idle` and `error` both start a fresh request. A failed lookup must not
    // permanently block access checks; the next setUser() call retries once.
    return this.load(userId, false);
  }

  refreshForUser(userId: string | number | null | undefined): void {
    if (userId === null || userId === undefined) {
      return;
    }
    if (
      this.sameUser(this.state.userId, userId) &&
      (this.state.status === 'loading' || this.state.status === 'refreshing')
    ) {
      return;
    }
    this.load(userId, this.sameUser(this.state.userId, userId)).subscribe();
  }

  clear(): void {
    this.requestSubscription?.unsubscribe();
    this.requestSubscription = null;
    this.stateSubject.next({
      userId: null,
      status: 'idle',
      items: [],
      error: null,
    });
  }

  stateForUser(userId: string | number | null | undefined): UserGroupsState | null {
    return userId !== null && userId !== undefined && this.sameUser(this.state.userId, userId)
      ? this.state
      : null;
  }

  hasActiveGroup(userId: string | number | null | undefined, slug: string): boolean {
    return (
      this.stateForUser(userId)?.items.some(
        (group) => group.slug === slug && group.is_active === true
      ) ?? false
    );
  }

  private load(userId: string | number, isRefresh: boolean): Observable<UserGroupsState> {
    this.requestSubscription?.unsubscribe();
    const previousState = this.state;
    const sameUser = this.sameUser(previousState.userId, userId);
    const retainedItems = sameUser ? previousState.items : [];
    const state: UserGroupsState = {
      userId,
      status: isRefresh && retainedItems.length > 0 ? 'refreshing' : 'loading',
      items: retainedItems,
      error: null,
    };
    this.stateSubject.next(state);

    this.requestSubscription = this.baseHttp
      .get('/user/groups')
      .pipe(timeout(USER_GROUPS_REQUEST_TIMEOUT_MS))
      .subscribe({
        next: (response: UserGroupsResponse) => {
          if (!this.sameUser(this.state.userId, userId)) {
            return;
          }
          if (
            !response ||
            !response.data ||
            !Array.isArray(response.data.items) ||
            response.data.items.some(
              (group) =>
                !group || typeof group.slug !== 'string' || typeof group.is_active !== 'boolean'
            )
          ) {
            this.fail(userId, new Error('Invalid response from GET /user/groups.'));
            return;
          }
          this.stateSubject.next({
            userId,
            status: 'loaded',
            items: response.data.items,
            error: null,
          });
        },
        error: (error: unknown) => this.fail(userId, error),
      });

    return this.whenSettled(userId);
  }

  private fail(userId: string | number, error: unknown): void {
    if (!this.sameUser(this.state.userId, userId)) {
      return;
    }
    this.stateSubject.next({
      userId,
      status: 'error',
      items: this.state.items,
      error,
    });
    console.error('Failed to load user groups from GET /api/user/groups.', error);
  }

  private whenSettled(userId: string | number): Observable<UserGroupsState> {
    return this.state$.pipe(
      filter(
        (state) =>
          !this.sameUser(state.userId, userId) ||
          state.status === 'loaded' ||
          state.status === 'error'
      ),
      take(1),
      filter((state) => this.sameUser(state.userId, userId))
    );
  }

  private sameUser(left: string | number | null, right: string | number | null): boolean {
    return left !== null && right !== null && String(left) === String(right);
  }
}
